/**
 * Registers the pwa-kit service worker. Call once at the app root (e.g. in
 * main.tsx after render). No-ops during dev (HMR) and where SW is unsupported.
 * Safe to call unconditionally.
 *
 * The SW uses skipWaiting + clients.claim, so a new deploy activates
 * immediately; with `autoReloadOnUpdate` the page also reloads once on takeover,
 * guaranteeing the freshest assets without the user knowing a SW exists.
 */
export function registerPwaSW(opts = {}) {
    const { swUrl = "/sw.js", force = false, autoReloadOnUpdate = true } = opts;
    if (typeof window === "undefined" || !("serviceWorker" in navigator))
        return;
    // Dev gate: import.meta.env.PROD is false under `vite dev`.
    const isProd = import.meta.env?.PROD ?? true;
    if (!isProd && !force)
        return;
    if (autoReloadOnUpdate) {
        // Reload when a *new* SW takes control (a deploy). hadController guards the
        // first-ever install's clients.claim() so we don't reload on first visit.
        const hadController = Boolean(navigator.serviceWorker.controller);
        let reloading = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
            if (!hadController || reloading)
                return;
            reloading = true;
            window.location.reload();
        });
    }
    const doRegister = () => {
        navigator.serviceWorker
            .register(swUrl, { scope: "/" })
            .then((reg) => {
            // Proactively pull a new SW version on every load.
            reg.update?.().catch(() => { });
        })
            .catch(() => {
            // registration failure must never break the app
        });
    };
    // If `load` already fired (fast/cached boot), register now; else wait for it.
    if (document.readyState === "complete")
        doRegister();
    else
        window.addEventListener("load", doRegister);
}
/**
 * Nuclear "get the latest version" escape hatch: unregister every service
 * worker, delete every cache, then reload. Wire to a button when a user might
 * be stuck on a stale build. Always reloads, even if the cleanup throws.
 * 🔴 Unregistering the service worker destroys its push subscription, and on iOS standalone it is worse: new
 * subscriptions are accepted by Apple (201) but webpushd never wakes this web app's SW again until the icon is
 * re-added (StaySubscribed 05.10, Inboxar 10.10). So on iOS standalone we only update() the registration.
 */
/** Чистая проверка: можно ли снимать регистрацию SW. На iOS в установленном веб-приложении — нельзя: после unregister
 *  Apple принимает новые подписки (201), а webpushd перестаёт будить SW этого приложения до переустановки иконки
 *  (StaySubscribed 05.10, Inboxar 10.10). */
export function mayUnregisterSw(ua, standalone) {
    const ios = /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1);
    return !(ios && standalone);
}
export async function resetPwaApp() {
    try {
        if ("serviceWorker" in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            const standalone = (typeof matchMedia === "function" && matchMedia("(display-mode: standalone)").matches) || navigator.standalone === true;
            if (mayUnregisterSw(navigator.userAgent, standalone))
                await Promise.all(regs.map((r) => r.unregister()));
            else
                await Promise.all(regs.map((r) => r.update().catch(() => undefined))); // iOS standalone: только обновить — регистрация и push-подписка остаются
        }
        if (typeof caches !== "undefined") {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
        }
    }
    catch {
        // fall through to reload regardless
    }
    finally {
        // caches gone (+ SW gone where it is safe) → this reload fetches everything fresh from network.
        window.location.reload();
    }
}
//# sourceMappingURL=register.js.map